/** Lead detail: figures, line items, commissions and the proposal file versions. */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  ListPlus,
  Lock,
  Pencil,
  RotateCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import { api, ApiError, getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { QuotationsCard } from '../purchasing/QuotationsCard';
import { LeadCommissionsCard } from './CommissionsCard';
import { peso, percent, shortDate, dateTime } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  filterClass,
  inputClass,
} from '../../components/ui';

interface ProposalFile {
  id: string;
  version: number;
  isFinal: boolean;
  kind?: 'PROPOSAL' | 'BOM';
  uploadedAt: string;
  file: { originalName: string; size: number; mimeType: string };
}

/** A row read out of the Word proposal, before anyone has approved it. */
interface ReadItem {
  name: string;
  description: string | null;
  unit: string | null;
  qty: string;
  unitPrice: string;
  amountVatEx: string;
}

interface ReadResult {
  found: boolean;
  reason: string | null;
  items: ReadItem[];
  itemsTotal: string;
  contractVatEx: string;
  contractGross: string;
  vatRate: string;
  /** 'EX' or 'INC' when the totals make it obvious; null when they do not. */
  suggestedBasis: 'EX' | 'INC' | null;
}

const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

interface LeadDetail {
  id: string;
  code: string;
  projectName: string;
  proposalRef: string | null;
  bomId: string | null;
  bomDriveUrl: string | null;
  proposalDate: string | null;
  validityDays: number | null;
  expectedCloseDate: string | null;
  notes: string | null;
  amountVatEx: string;
  vat: string;
  vatApplicable: boolean;
  taxClass: string;
  totalContractPrice: string;
  ewtAmount: string;
  wvatAmount: string;
  expectedNetCollection: string;
  projectCost?: string;
  grossProfit?: string;
  grossMarginPct?: string;
  totalCommission?: string;
  /** Project cost plus commissions (with both money rights). */
  totalCostWithCommission?: string;
  netProfit?: string;
  netMarginPct?: string;
  weightedAmount: string;
  isGovernment: boolean;
  locked: boolean;
  confirmedAt: string | null;
  customer: { id: string; name: string; code: string; status: string };
  contact: { name: string; designation: string | null } | null;
  stage: { label: string; code: string };
  solutionCategory: { label: string } | null;
  owner: { name: string };
  items: { id: string; lineNo: number; name: string; description: string | null; unit: string | null; qty: string; amountVatEx: string }[];
  commissions?: { id: string; payee: string; amount: string; ewtAmount: string; netPayout: string; category: { label: string } }[];
  files: ProposalFile[];
  salesOrders: { id: string; code: string }[];
}

interface Readiness {
  ready: boolean;
  problems: { code: string; message: string }[];
}

interface ParameterGroup {
  key: string;
  values: { id: string; code: string; label: string }[];
}

export function LeadDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [confirmForm, setConfirmForm] = useState({
    confirmedAt: new Date().toISOString().slice(0, 10),
    clientPoRef: '',
    paymentTermsId: '',
    withRecurring: false,
    mrcAmountVatEx: '',
    recurringStart: '',
    recurringMonths: '12',
    billingDay: '5',
    autoIssue: false,
  });

  const lead = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api<{ data: LeadDetail }>(`/leads/${id}`),
  });

  /**
   * The lead and its readiness are separate queries, so anything that changes
   * one changes the other. Refreshing only the lead left the confirm dialog
   * insisting on a proposal that had just been uploaded.
   */
  const refreshLead = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['lead', id] });
    void queryClient.invalidateQueries({ queryKey: ['lead-readiness', id] });
  };

  const readiness = useQuery({
    queryKey: ['lead-readiness', id],
    queryFn: () => api<Readiness>(`/leads/${id}/readiness`),
  });

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });

  const confirmLead = useMutation({
    mutationFn: () =>
      api<{ data: { salesOrder: { code: string }; project: { code: string; deliverables: number } } }>(
        `/leads/${id}/confirm`,
        {
          method: 'POST',
          body: {
            confirmedAt: confirmForm.confirmedAt || undefined,
            clientPoRef: confirmForm.clientPoRef || undefined,
            paymentTermsId: confirmForm.paymentTermsId || undefined,
            ...(confirmForm.withRecurring
              ? {
                  mrcAmountVatEx: Number(confirmForm.mrcAmountVatEx || 0),
                  recurringStart: confirmForm.recurringStart || undefined,
                  recurringMonths: Number(confirmForm.recurringMonths || 12),
                  billingDay: Number(confirmForm.billingDay || 1),
                  autoIssue: confirmForm.autoIssue,
                }
              : {}),
          },
        },
      ),
    onSuccess: (result) => {
      setError(null);
      setConfirming(false);
      setNotice(
        `Confirmed. ${result.data.salesOrder.code} and project ${result.data.project.code} created, with ${result.data.project.deliverables} cards on the board.`,
      );
      refreshLead();
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not confirm the lead.'),
  });

  const reopenLead = useMutation({
    mutationFn: () =>
      api<{ message: string }>(`/leads/${id}/reopen`, {
        method: 'POST',
        body: { reason: reopenReason.trim() },
      }),
    onSuccess: (result) => {
      setError(null);
      setReopening(false);
      setReopenReason('');
      setNotice(result.message);
      refreshLead();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not reopen the lead.'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      body.append('isFinal', 'true');
      const response = await fetch(`/api/v1/leads/${id}/files`, {
        method: 'POST',
        credentials: 'include',
        headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(response.status, payload?.message ?? 'Upload failed.');
      }
      return response.json();
    },
    onSuccess: () => {
      setError(null);
      setNotice('Proposal uploaded and marked final.');
      refreshLead();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not upload the file.'),
  });

  /** Rows read from the Word proposal, waiting to be checked. */
  const [readRows, setReadRows] = useState<ReadItem[] | null>(null);
  const [readMeta, setReadMeta] = useState<ReadResult | null>(null);
  /** Set from the totals when they make it obvious, not assumed. */
  const [readVatIncluded, setReadVatIncluded] = useState(false);

  const readItems = useMutation({
    mutationFn: (fileRecordId: string) =>
      api<{ data: ReadResult }>(`/leads/${id}/files/${fileRecordId}/read-items`, {
        method: 'POST',
      }),
    onSuccess: (result) => {
      setError(null);
      if (!result.data.found) {
        setNotice(null);
        setError(result.data.reason ?? 'Nothing could be read from that proposal.');
        return;
      }
      setReadMeta(result.data);
      // The totals usually say which basis the proposal is priced on.
      setReadVatIncluded(result.data.suggestedBasis === 'INC');
      setReadRows(result.data.items);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not read that proposal.'),
  });

  const saveReadItems = useMutation({
    mutationFn: ({ items, setContract }: { items: ReadItem[]; setContract: boolean }) =>
      api(`/leads/${id}`, {
        method: 'PATCH',
        body: {
          // The server strips VAT when the figures were priced with it in.
          amountsIncludeVat: readVatIncluded,
          // The proposal is what was sent to the customer, so it can be made
          // the contract rather than leaving the two disagreeing.
          ...(setContract
            ? {
                amountVatEx: items.reduce((total, item) => total + Number(item.amountVatEx), 0),
              }
            : {}),
          items: items.map((item) => ({
            name: item.name,
            description: item.description ?? undefined,
            unit: item.unit ?? undefined,
            qty: Number(item.qty),
            amountVatEx: Number(item.amountVatEx),
          })),
        },
      }),
    onSuccess: () => {
      setReadRows(null);
      setReadMeta(null);
      setError(null);
      setNotice('Items taken from the proposal.');
      refreshLead();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save those items.'),
  });

  const markFinal = useMutation({
    mutationFn: (fileRecordId: string) =>
      api(`/leads/${id}/files/${fileRecordId}/final`, { method: 'POST' }),
    onSuccess: () => refreshLead(),
  });

  const openFile = async (fileRecordId: string, inline: boolean): Promise<void> => {
    const response = await fetch(
      `/api/v1/leads/files/${fileRecordId}/download?inline=${String(inline)}`,
      { credentials: 'include', headers: { authorization: `Bearer ${getAccessToken() ?? ''}` } },
    );
    if (!response.ok) {
      setError('Could not open the file.');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (lead.isLoading) return <Skeleton className="h-96 w-full" />;
  const row = lead.data?.data;
  if (!row) return <Alert tone="critical">Lead not found.</Alert>;

  const showMoney = can('leads.view_financials');
  const finalFile = row.files.find((file) => file.isFinal);
  const hasProposal = row.files.some((file) => file.kind !== 'BOM');
  const salesOrder = row.salesOrders[0] ?? null;
  const paymentTerms = parameters.data?.data.find((g) => g.key === 'payment_terms')?.values ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-navy dark:text-white">{row.projectName}</h1>
            <StatusChip tone={row.stage.code === 'CLOSED_WON' ? 'ok' : 'info'}>
              {row.stage.label}
            </StatusChip>
            {row.locked && (
              <StatusChip tone="neutral">
                <Lock aria-hidden="true" className="h-3 w-3" /> Locked
              </StatusChip>
            )}
            {row.isGovernment && <StatusChip tone="info">Government</StatusChip>}
          </div>
          <p className="text-sm text-slate-500">
            <span className="font-mono">{row.code}</span> ·{' '}
            <Link to={`/customers/${row.customer.id}`} className="hover:underline">
              {row.customer.name}
            </Link>
            {row.contact ? ` · ${row.contact.name}` : ''} · owner {row.owner.name}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {salesOrder && (
            <Link to={`/sales-orders/${salesOrder.id}`}>
              <Button variant="secondary">
                <CheckCircle2 aria-hidden="true" className="h-4 w-4" /> {salesOrder.code}
              </Button>
            </Link>
          )}
          {!row.locked && can('leads.update') && (
            <Link to={`/leads/${row.id}/edit`}>
              <Button variant="secondary">
                <Pencil aria-hidden="true" className="h-4 w-4" /> Edit
              </Button>
            </Link>
          )}
          {!row.locked && can('leads.confirm') && (
            <Button onClick={() => setConfirming(true)}>
              <CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Confirm
            </Button>
          )}
          {row.locked && can('leads.reopen') && (
            <Button variant="secondary" onClick={() => setReopening(true)}>
              <RotateCcw aria-hidden="true" className="h-4 w-4" /> Reopen
            </Button>
          )}
        </div>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
      {row.locked && (
        <Alert tone="info">
          This lead was confirmed on {shortDate(row.confirmedAt)} and is read-only. Management can
          reopen it while the sales order has no issued billing.
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card title="Money" className="lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <Figure label="Amount (VAT-ex)" value={peso(row.amountVatEx)} />
            <Figure label={`VAT${row.vatApplicable ? '' : ' (exempt)'}`} value={peso(row.vat)} />
            <Figure label="Total contract price" value={peso(row.totalContractPrice)} strong />
            <Figure label="Expected EWT" value={peso(row.ewtAmount)} />
            {row.isGovernment && <Figure label="Withholding VAT" value={peso(row.wvatAmount)} />}
            <Figure label="Expected net collection" value={peso(row.expectedNetCollection)} strong />
            {showMoney && (
              <>
                <Figure label="Project cost" value={peso(row.projectCost)} />
                <Figure
                  label="Gross profit"
                  value={peso(row.grossProfit)}
                  sub={percent(row.grossMarginPct)}
                />
                {row.totalCommission !== undefined && (
                  <Figure label="Commissions" value={peso(row.totalCommission)} />
                )}
                {row.totalCostWithCommission !== undefined && (
                  <Figure label="Cost incl. commissions" value={peso(row.totalCostWithCommission)} />
                )}
                <Figure
                  label="Net profit"
                  value={peso(row.netProfit)}
                  sub={percent(row.netMarginPct)}
                  danger={Number(row.netProfit ?? 0) < 0}
                />
              </>
            )}
          </div>
        </Card>

        <Card title="Proposal">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Reference</dt>
              <dd className="font-mono">{row.proposalRef ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">BOM</dt>
              <dd>
                {row.bomId ?? '—'}
                {row.bomDriveUrl && (
                  <a
                    href={row.bomDriveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-flex items-center gap-1 text-brand hover:underline"
                  >
                    Open <ExternalLink aria-hidden="true" className="h-3 w-3" />
                  </a>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Dates</dt>
              <dd>
                Proposal {shortDate(row.proposalDate)} · valid {row.validityDays ?? '—'} days ·
                expected close {shortDate(row.expectedCloseDate)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Solution</dt>
              <dd>{row.solutionCategory?.label ?? '—'}</dd>
            </div>
          </dl>
        </Card>
      </div>

      <QuotationsCard leadId={row.id} />

      <Card title={`Line items (${row.items.length})`}>
        {row.items.length === 0 ? (
          <EmptyState
            title="No line items"
            description="Not needed — the proposal and BOM carry the detail, and deliverables are set up on the project board once the lead is confirmed."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Deliverable</Th>
                <Th>Unit</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Amount (VAT-ex)</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {row.items.map((item) => (
                <tr key={item.id}>
                  <Td>{item.lineNo}</Td>
                  <Td>
                    <p className="font-medium">{item.name}</p>
                    {item.description && (
                      <p className="text-xs text-slate-500">{item.description}</p>
                    )}
                  </Td>
                  <Td>{item.unit ?? '—'}</Td>
                  <Td align="right" className="tabular">
                    {item.qty}
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(item.amountVatEx)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <LeadCommissionsCard leadId={row.id} locked={row.locked} isGovernment={row.isGovernment} />

      <Card
        title="Proposal file"
        description="PDF or the Word original, up to 25 MB. Every upload is kept; exactly one is the final version. Upload the .docx and its item list can be read straight into the lead."
        actions={
          !row.locked &&
          can('leads.update') && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) upload.mutate(file);
                  event.target.value = '';
                }}
              />
              <Button size="sm" busy={upload.isPending} onClick={() => fileInput.current?.click()}>
                <Upload aria-hidden="true" className="h-4 w-4" /> Upload proposal
              </Button>
            </>
          )
        }
      >
        {row.files.length === 0 ? (
          <EmptyState
            title="No proposal uploaded yet"
            description="A final proposal is required before the lead can be confirmed. Upload the Word original and its items can be read in."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Version</Th>
                <Th>File</Th>
                <Th>Uploaded</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {row.files.map((file) => (
                <tr key={file.id}>
                  <Td>
                    <span className="inline-flex items-center gap-2">
                      <FileText aria-hidden="true" className="h-4 w-4 text-slate-400" />v
                      {file.version}
                      {file.kind === 'BOM' && <StatusChip tone="neutral">BOM</StatusChip>}
                      {file.isFinal && <StatusChip tone="ok">Final</StatusChip>}
                    </span>
                  </Td>
                  <Td>
                    <p>{file.file.originalName}</p>
                    <p className="text-xs text-slate-500">
                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </Td>
                  <Td>{dateTime(file.uploadedAt)}</Td>
                  <Td align="right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => void openFile(file.id, true)}>
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void openFile(file.id, false)}
                      >
                        Download
                      </Button>
                      {file.file.mimeType === DOCX && !row.locked && can('leads.update') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          busy={readItems.isPending}
                          onClick={() => readItems.mutate(file.id)}
                        >
                          <ListPlus aria-hidden="true" className="h-4 w-4" /> Read items
                        </Button>
                      )}
                      {!file.isFinal && file.kind !== 'BOM' && !row.locked && can('leads.update') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => markFinal.mutate(file.id)}
                        >
                          Mark final
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
        {!finalFile && hasProposal && (
          <div className="mt-3">
            <Alert tone="warning">No version is marked final yet.</Alert>
          </div>
        )}
      </Card>

      {row.notes && (
        <Card title="Notes">
          <p className="whitespace-pre-wrap text-sm">{row.notes}</p>
        </Card>
      )}

      {readRows && (() => {
        /* What each figure becomes once VAT is stripped, if it is being. */
        const rate = Number(readMeta?.vatRate ?? 0.12);
        const toSaved = (amount: string): string =>
          (readVatIncluded ? Number(amount) / (1 + rate) : Number(amount)).toFixed(2);
        const savedTotal = readRows.reduce(
          (total, item) => total + Number(toSaved(item.amountVatEx)),
          0,
        );
        return (
        <Modal
          title="Items read from the proposal"
          description="Check every line before saving. These replace the lead's current items."
          onClose={() => setReadRows(null)}
        >
          <div className="space-y-3">
            <Alert tone="warning">
              Read from the Word file, not typed — check the quantities and prices against the
              proposal before saving.
            </Alert>

            <label className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-200">
                Prices in the proposal are
              </span>
              <select
                className={`${filterClass} w-44`}
                value={readVatIncluded ? 'INC' : 'EX'}
                onChange={(event) => setReadVatIncluded(event.target.value === 'INC')}
              >
                <option value="EX">VAT exclusive</option>
                <option value="INC">VAT inclusive</option>
              </select>
              {readMeta?.suggestedBasis && (
                <span className="text-xs text-slate-500">
                  Set from the totals: they match the contract
                  {readMeta.suggestedBasis === 'INC' ? ' once VAT is taken off' : ' as they are'}.
                </span>
              )}
            </label>

            <TableWrap>
              <thead>
                <tr>
                  <Th>Particular</Th>
                  <Th>Unit</Th>
                  <Th align="right">Qty</Th>
                  <Th align="right">Unit price</Th>
                  <Th align="right">As read</Th>
                  <Th align="right">Will be saved</Th>
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {readRows.map((item, index) => (
                  <tr key={index}>
                    <Td>
                      <p>{item.name}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500">{item.description}</p>
                      )}
                    </Td>
                    <Td>{item.unit ?? '—'}</Td>
                    <Td align="right">{Number(item.qty)}</Td>
                    <Td align="right">{peso(item.unitPrice)}</Td>
                    <Td align="right">{peso(item.amountVatEx)}</Td>
                    <Td align="right" className="font-semibold">
                      {peso(toSaved(item.amountVatEx))}
                    </Td>
                    <Td>
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={`Drop ${item.name}`}
                        onClick={() =>
                          setReadRows(readRows.filter((_, position) => position !== index))
                        }
                      >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <div className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
              <div className="flex justify-between">
                <span className="text-slate-500">These lines will be saved as</span>
                <span className="tabular font-bold">{peso(savedTotal.toFixed(2))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Contract on this lead (VAT-exclusive)</span>
                <span className="tabular">{peso(row.amountVatEx)}</span>
              </div>
              {Math.abs(savedTotal - Number(row.amountVatEx)) > 1 && (
                <div className="mt-2">
                  <Alert tone="warning">
                    These do not match, a difference of{' '}
                    {peso(Math.abs(savedTotal - Number(row.amountVatEx)).toFixed(2))}. Either
                    switch the VAT setting above, or set the contract to what the proposal says.
                  </Alert>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setReadRows(null)}>
                Cancel
              </Button>
              {Math.abs(savedTotal - Number(row.amountVatEx)) > 1 && (
                <Button
                  variant="secondary"
                  busy={saveReadItems.isPending}
                  onClick={() => saveReadItems.mutate({ items: readRows, setContract: true })}
                >
                  Save and set the contract to {peso(savedTotal.toFixed(2))}
                </Button>
              )}
              <Button
                busy={saveReadItems.isPending}
                disabled={readRows.length === 0}
                onClick={() => saveReadItems.mutate({ items: readRows, setContract: false })}
              >
                <ListPlus aria-hidden="true" className="h-4 w-4" /> Save {readRows.length} item
                {readRows.length === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </Modal>
        );
      })()}

      {confirming && (
        <Modal
          title="Confirm this lead"
          description="This is the point of no return for the proposal: it becomes a contract."
          onClose={() => setConfirming(false)}
        >
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800">
              <p className="mb-2 font-semibold">What happens when you confirm:</p>
              <ul className="list-disc space-y-1 pl-5 text-slate-600 dark:text-slate-300">
                <li>The lead moves to Closed Won and becomes read-only.</li>
                <li>A sales order is created with today's amounts frozen onto it.</li>
                <li>
                  {row.items.length > 0
                    ? `A project is created with ${row.items.length} card${row.items.length === 1 ? '' : 's'} on its board, one per line item.`
                    : 'A project is created with an empty board, ready for its deliverables.'}
                </li>
              </ul>
            </div>

            {readiness.data && !readiness.data.ready && (
              <Alert tone="critical">
                <p className="mb-1 font-semibold">This lead is not ready yet:</p>
                <ul className="list-disc space-y-1 pl-5">
                  {readiness.data.problems.map((problem) => (
                    <li key={problem.code}>{problem.message}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                confirmLead.mutate();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Confirmation date" required>
                  <input
                    className={inputClass}
                    type="date"
                    required
                    value={confirmForm.confirmedAt}
                    onChange={(event) =>
                      setConfirmForm({ ...confirmForm, confirmedAt: event.target.value })
                    }
                  />
                </Field>
                <Field label="Client PO / contract reference">
                  <input
                    className={inputClass}
                    value={confirmForm.clientPoRef}
                    onChange={(event) =>
                      setConfirmForm({ ...confirmForm, clientPoRef: event.target.value })
                    }
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Payment terms" required>
                    <select
                      className={inputClass}
                      required
                      value={confirmForm.paymentTermsId}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, paymentTermsId: event.target.value })
                      }
                    >
                      <option value="">Choose terms</option>
                      {paymentTerms.map((term) => (
                        <option key={term.id} value={term.id}>
                          {term.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                  checked={confirmForm.withRecurring}
                  onChange={(event) =>
                    setConfirmForm({ ...confirmForm, withRecurring: event.target.checked })
                  }
                />
                This contract also has a monthly recurring charge
              </label>

              {confirmForm.withRecurring && (
                <div className="grid gap-4 rounded-xl border border-slate-200 p-3 sm:grid-cols-2 dark:border-slate-700">
                  <Field label="Monthly charge (VAT-ex)" required>
                    <input
                      className={`${inputClass} tabular`}
                      type="number"
                      step="0.01"
                      required
                      value={confirmForm.mrcAmountVatEx}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, mrcAmountVatEx: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Starting month" required>
                    <input
                      className={inputClass}
                      type="date"
                      required
                      value={confirmForm.recurringStart}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, recurringStart: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Number of months">
                    <input
                      className={inputClass}
                      type="number"
                      min={1}
                      max={120}
                      value={confirmForm.recurringMonths}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, recurringMonths: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Billing day of month" hint="1 to 28.">
                    <input
                      className={inputClass}
                      type="number"
                      min={1}
                      max={28}
                      value={confirmForm.billingDay}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, billingDay: event.target.value })
                      }
                    />
                  </Field>
                  <label className="flex items-center gap-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                      checked={confirmForm.autoIssue}
                      onChange={(event) =>
                        setConfirmForm({ ...confirmForm, autoIssue: event.target.checked })
                      }
                    />
                    Issue each monthly statement automatically
                  </label>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="submit"
                  busy={confirmLead.isPending}
                  disabled={readiness.data ? !readiness.data.ready : true}
                >
                  <CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Confirm the lead
                </Button>
                <Button type="button" variant="secondary" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {reopening && (
        <Modal
          title="Reopen this lead"
          description="Only possible while no billing has been issued. The sales order and project are voided."
          onClose={() => setReopening(false)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              reopenLead.mutate();
            }}
          >
            <Field label="Reason" hint="Recorded in the audit log." required>
              <textarea
                className={`${inputClass} h-24`}
                required
                minLength={5}
                value={reopenReason}
                onChange={(event) => setReopenReason(event.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="danger" busy={reopenLead.isPending}>
                Reopen the lead
              </Button>
              <Button type="button" variant="secondary" onClick={() => setReopening(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  sub,
  strong,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
  danger?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-800">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={`tabular ${strong ? 'text-lg font-bold' : 'text-base font-semibold'} ${
          danger ? 'text-red-600' : 'text-navy dark:text-white'
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
